import React from "react";
import { Clock, X } from "lucide-react";
import { useI18n } from "../../i18n/I18nProvider";

const KEY = "ojavana.officeHoursDismissed";

function isOpenNow() {
  // 08:00 – 19:00 CET, Monday–Friday. We approximate CET ≈ UTC+1 (use Europe/Zurich via Intl).
  try {
    const fmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Zurich",
      hour: "numeric",
      minute: "numeric",
      weekday: "short",
      hour12: false,
    });
    const parts = fmt.formatToParts(new Date());
    const get = (t) => parts.find((p) => p.type === t)?.value;
    const wd = get("weekday");
    const h = parseInt(get("hour") || "0", 10);
    const isWeekday = ["Mon", "Tue", "Wed", "Thu", "Fri"].includes(wd);
    return isWeekday && h >= 8 && h < 19;
  } catch {
    return true;
  }
}

export default function OfficeHoursBanner() {
  const { t } = useI18n();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(KEY) === "1") return;
    setOpen(isOpenNow());
    const t = setInterval(() => setOpen(isOpenNow()), 60_000);
    return () => clearInterval(t);
  }, []);

  if (!open) return null;

  // Sit just below the fixed header (h-16 / md:h-20). A higher z-index used to
  // cover the desktop nav ("Bureau de Boudry ouvert · 08:00–19:00 CET").
  return (
    <div
      data-testid="office-hours-banner"
      className="fixed top-16 md:top-20 mt-2 left-1/2 -translate-x-1/2 z-40 max-w-[calc(100vw-1.5rem)] inline-flex items-center gap-3 rounded-full bg-oj-deep text-white text-xs sm:text-sm font-medium px-3 py-1.5 shadow-lg border border-white/10"
      role="status"
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-oj-brand opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-oj-brand" />
      </span>
      <Clock className="size-3.5 text-oj-brand" />
      <span>{t("banner.office_open")}</span>
      <button
        aria-label="Dismiss"
        onClick={() => {
          window.sessionStorage.setItem(KEY, "1");
          setOpen(false);
        }}
        className="ml-1 -mr-1 size-5 grid place-items-center rounded-full hover:bg-white/10"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
